import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, CalendarClock, FileSearch, MessageSquare, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AdvancedAIChat } from '@/components/AdvancedAIChat';
import { DeadlineCalculator, type DeadlinePreset } from '@/components/ai/DeadlineCalculator';
import { DecisionReader } from '@/components/ai/DecisionReader';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import { type AITaskType } from '@/hooks/useAI';
import { assistants } from '@/config/aiConfig';

const AICenter = () => {
  const location = useLocation();
  const caseIdFromLocation = (location.state as { caseId?: string })?.caseId;

  const [activeTab, setActiveTab] = useState(caseIdFromLocation ? 'assistants' : 'deadlines');
  const [selectedAssistant, setSelectedAssistant] = useState<AITaskType>(caseIdFromLocation ? 'case_analysis' : 'general');
  const [initialPrompt, setInitialPrompt] = useState<string | undefined>(undefined);
  const [deadlinePreset, setDeadlinePreset] = useState<DeadlinePreset | undefined>(undefined);

  const { suggestedPrompts } = useCaseDetails(caseIdFromLocation);
  const selected = assistants.find((a) => a.id === selectedAssistant) || assistants[0];

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9a7f52]">Ferramentas</p>
        <h1 className="mt-1 flex items-center gap-2 text-3xl font-semibold tracking-tight text-slate-900">
          <Sparkles className="h-6 w-6 text-[#c4ab83]" />
          Centro de IA Jurídica
        </h1>
        <p className="mt-1 text-sm text-slate-500">Conte prazos, leia decisões e prepare rascunhos.</p>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-5">
        <TabsList className="grid h-auto w-full grid-cols-3 rounded-xl bg-slate-100 p-1">
          <TabsTrigger value="deadlines" className="gap-1.5 rounded-lg py-2 text-xs sm:text-sm"><CalendarClock className="hidden h-4 w-4 sm:block" />Calculadora de Prazos</TabsTrigger>
          <TabsTrigger value="reader" className="gap-1.5 rounded-lg py-2 text-xs sm:text-sm"><FileSearch className="hidden h-4 w-4 sm:block" />Leitor de Decisões</TabsTrigger>
          <TabsTrigger value="assistants" className="gap-1.5 rounded-lg py-2 text-xs sm:text-sm"><MessageSquare className="hidden h-4 w-4 sm:block" />Assistentes</TabsTrigger>
        </TabsList>

        {/* Mantida montada para o Leitor poder enviar o prazo sem perder o que já foi preenchido */}
        <TabsContent value="deadlines" forceMount className="data-[state=inactive]:hidden">
          <DeadlineCalculator preset={deadlinePreset} />
        </TabsContent>

        <TabsContent value="reader" forceMount className="data-[state=inactive]:hidden">
          <DecisionReader
            onUseDeadline={(deadline, caseNumber) => {
              setDeadlinePreset({
                days: deadline.dias,
                mode: deadline.contagem === 'corridos' ? 'corridos' : deadline.contagem === 'uteis' ? 'uteis' : undefined,
                description: deadline.descricao,
                caseNumber,
                nonce: Date.now(),
              });
              setActiveTab('deadlines');
            }}
          />
        </TabsContent>

        <TabsContent value="assistants" className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {assistants.map((assistant) => {
              const Icon = assistant.icon;
              const isSelected = selectedAssistant === assistant.id;
              return (
                <button
                  key={assistant.id}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => { setSelectedAssistant(assistant.id); setInitialPrompt(undefined); }}
                  className={`rounded-2xl border p-4 text-left transition-colors ${isSelected ? 'border-[#c4ab83] bg-[#c4ab83]/10' : 'border-slate-200 bg-white hover:border-[#c4ab83]/60'}`}
                >
                  <Icon className="h-5 w-5 text-[#9a7f52]" />
                  <p className="mt-2 text-sm font-semibold text-slate-900">{assistant.title}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{assistant.description}</p>
                </button>
              );
            })}
          </div>

          <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>A IA pode errar e citar leis ou julgados que não existem. Confira tudo na fonte oficial. As perguntas são enviadas ao Google (Gemini) e ficam no histórico do sistema, então evite dados sigilosos.</p>
          </div>

          {caseIdFromLocation && suggestedPrompts.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {suggestedPrompts.map((prompt) => (
                <Button
                  key={prompt}
                  variant="outline"
                  size="sm"
                  className="h-auto whitespace-normal text-left text-xs"
                  onClick={() => { setSelectedAssistant('case_analysis'); setInitialPrompt(prompt); }}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          )}

          <AdvancedAIChat
            key={selectedAssistant}
            initialTaskType={selectedAssistant}
            title={selected.title}
            placeholder="Escreva seu pedido..."
            caseId={caseIdFromLocation}
            initialPrompt={initialPrompt}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AICenter;
